// Sidebar.js
import React, { useContext, useEffect, useState, useImperativeHandle, useRef } from 'react';
import './Sidebar.css';
import { AuthContext } from '../Login/AuthContext';
import api from '../api';
import textCounter from '../../store/WordCount';
import ValidationPopup from '../UI/ValidationPopup';
import ExportPopup from './Editor/ExportPopup';
import Popup from '../UI/Popup';
import LoadingButton from '../UI/LoadingButton';
import useProjectsManager from './ProjectsManager';

const Sidebar = React.forwardRef(({ onProjectSelected, onNewProject, isNewProject, selectedProject, onOpenPricing, onOpenSettings, onOpenGenerator, selected }, ref) => {

    const { subscription, user } = useContext(AuthContext);
    const [isCreatingProject, setIsCreatingProject] = useState(false);

    const {
        projects,
        currentProject,
        setCurrentProject,
        loading,
        showProjectDeletePopup,
        displayDeletePopup,
        setDisplayDeletePopup,
        deleteProjectData,
        isDeleting,
        handleDeleteProject,
        refreshProjects,
        editingProjectId,
        editingProjectName,
        setEditingProjectName,
        startEditingProject,
        cancelEditingProject,
        updateProjectName,
        handleEditKeyDown,
        isUpdatingName
    } = useProjectsManager({ onProjectSelected, onNewProject });

    const { wordCount, charCount } = textCounter();

    // Exposer refreshProjects via la ref
    useImperativeHandle(ref, () => ({
        refreshProjects: refreshProjects
    }));

    // Rafraîchir la liste des projets lorsque selectedProject change
    useEffect(() => {
        refreshProjects();
    }, [selectedProject]);
    
    // Rafraîchir la liste des projets au chargement initial
    useEffect(() => {
        refreshProjects();
    }, []);

    // Fonction pour créer directement un projet vide
    const createEmptyProject = async () => {
        setIsCreatingProject(true);
        
        try {
            const response = await api.post('/create_empty_project', {
                language: 'english',
                tone: 'Informative'
            });
            
            console.log("Empty project created successfully:", response.data);
            
            if (response.data && response.data.project_id) {
                onProjectSelected(response.data.project_id);
                refreshProjects();
            } else {
                console.error("No project_id returned from server");
            }
        } catch (error) {
            console.error('Error creating empty project:', error);
        } finally {
            setIsCreatingProject(false);
        }
    };

    return (
        <>
            <div className="side-menu">
                <img className='logo' src="/logo_light.webp" alt="Logo" style={{ width: '30%', marginBottom: '30px' }} />

                <div className="nav">
                    <LoadingButton 
                        className="button-important"
                        onClick={createEmptyProject}
                        isLoading={isCreatingProject}
                    >
                        <div className="inner">
                            <span className="material-symbols-outlined icon">note_add</span>
                            Create New Project
                        </div>
                    </LoadingButton>

                    <button
                        className={`button-sidemenu ${selected === 'generator' ? 'active' : ''}`}
                        onClick={onOpenGenerator}
                    >
                        <span className="material-symbols-outlined icon">book_4</span>
                        Fast book generator
                    </button>

                    <h3>Projects</h3>

                    <div className="project-list">
                        {projects.map((project) => (
                            <li
                                key={project.id}
                                className={`project-item ${project.id === currentProject ? 'selected' : ''}`}
                                onClick={() => {
                                    if (editingProjectId !== project.id) {
                                        onProjectSelected(project.id);
                                        setCurrentProject(project.id);
                                    }
                                }}
                            >
                                {editingProjectId === project.id ? (
                                    <div className="project-edit-container" onClick={e => e.stopPropagation()}>
                                        <input
                                            type="text"
                                            className="project-name-input"
                                            value={editingProjectName}
                                            onChange={e => setEditingProjectName(e.target.value)}
                                            onKeyDown={e => handleEditKeyDown(e, project.id)}
                                            onBlur={() => updateProjectName(project.id)}
                                            autoFocus
                                        />
                                        {isUpdatingName && (
                                            <div className="input-loader">
                                                <span className="loader-small"></span>
                                            </div>
                                        )}
                                    </div>
                                ) : (
                                    <>
                                        <span className="project-name">{project.name}</span>
                                        <div className="project-actions">
                                            <button
                                                className="edit-button"
                                                onClick={(e) => startEditingProject(project, e)}
                                                title="Edit project name"
                                            >
                                                <span className="material-symbols-outlined icon">edit</span>
                                            </button>
                                            <button
                                                className="delete-button"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    showProjectDeletePopup(project);
                                                }}
                                                title="Delete project"
                                            >
                                                <span className="material-symbols-outlined icon">delete</span>
                                            </button>
                                        </div>
                                    </>
                                )}
                            </li>
                        ))}
                    </div>



                    <button
                        className={`button-sidemenu ${selected === 'settings' ? 'active' : ''}`}
                        onClick={onOpenSettings}
                    >
                        <span className="material-symbols-outlined icon">settings</span>
                        Settings
                    </button>

                </div>

                {/* {!subscription?.active && (
                    <div className="premium-cta">
                        <h3>Unlock Plus Features</h3>
                        <p>Create custom styles, manage tags, and make longer recordings.</p>
                        <button className="button-important" onClick={onOpenPricing}>
                            <div className="inner">
                                <span className="material-symbols-outlined icon">star</span>
                                Upgrade Now
                            </div>
                        </button>
                    </div>
                )} */}


                <div className="bottom-panel">

                    {/* Global user quota (plan-based) */}
                    {user && (
                        <PlanQuota user={user} subscription={subscription} />
                    )}

                    <p className="count-stats" style={{ textAlign: 'center', fontSize: '14px', fontWeight: '500' }}>
                        {wordCount} words / {charCount} symbols
                    </p>
                    {/* <button className="button-plain" onClick={() => window.open("https://echonote.canny.io", "_blank")}>
                        <span className="material-symbols-outlined icon">feedback</span>
                        Give feedback
                    </button> */}



                </div>



            </div>

            {displayDeletePopup && (
                <Popup
                    title="Delete project"
                    onClose={() => setDisplayDeletePopup(false)}
                >
                    <div className="">
                        <p>Do you really want to delete the project {deleteProjectData.name}?</p>
                        
                        <div className="popup-buttons">
                            <button 
                                className="button-hollow" 
                                onClick={() => setDisplayDeletePopup(false)}
                            >
                                Cancel
                            </button>
                            <LoadingButton 
                                className="button-plain"
                                onClick={handleDeleteProject}
                                isLoading={isDeleting}
                            >
                                Delete
                            </LoadingButton>
                        </div>
                    </div>
                </Popup>
            )}
        </>
    );
});

const PlanQuota = ({ user, subscription }) => {
    const [quota, setQuota] = useState({ current: 0, limit: 0, unlimited: false });

    useEffect(() => {
        let mounted = true;

        const fetchQuota = () => {
            api.get('/me/quota')
                .then(res => {
                    if (!mounted) return;
                    setQuota(res.data);
                })
                .catch(() => {});
        };

        fetchQuota();
        const id = setInterval(fetchQuota, 20000); // refresh every 20s

        return () => {
            mounted = false;
            clearInterval(id);
        };
    }, [subscription, user?.byok]);

    const limitLabel = quota.unlimited ? '∞' : quota.limit;
    const percent = quota.unlimited ? 100 : Math.min(100, Math.max(0, (quota.current / (quota.limit || 1)) * 100));

    // BYOK overrides display name
    const planName = user?.byok ? 'BYOK' : (subscription?.plan?.name || (!subscription?.payment_failed && subscription ? 'Premium' : 'Free'));

    return (
        <>
            <div className="token-stats">
                <p>{planName}</p>
                <p>{quota.current}/{limitLabel}</p>
            </div>
            <div className="token-progress">
                <div
                    className="token-progress-bar"
                    style={{ width: `${percent}%` }}
                />
            </div>
        </>
    );
};

export default Sidebar;
